import {
    AlertTriangle,
    ArrowDown,
    ArrowUp,
    CheckCircle2,
    ChevronDown,
    Ruler,
} from 'lucide-react';
import React, { useState } from 'react';

import { cn } from '@/lib/utils';

import { formatLevelPrice } from '../liqMap/copy';
import type { PositionType } from '../trading/types';

import { checkStop } from './check';
import { explainLevel, explainSource, explainStop } from './copy';
import type { Level } from './levels';
import { heldTimes, isStrong, nearestBySide } from './palette';

const CHIP_LABEL = {
    ok: 'Stop is clear',
    warn: 'Stop is close',
    danger: 'Stop on a level',
} as const;

interface SrLevelsLegendProps {
    coin: string;
    levels: readonly Level[];
    spot: number;
    /** how many bars the levels came from; 0 while loading or on a fresh listing */
    barCount: number;
    updatedAt: number;
    isLoading: boolean;
    isError: boolean;
    trade: { type: PositionType; stopLossPrice: number | null } | null;
    className?: string;
}

/**
 * The card that makes the level lines mean something. Collapsed, one chip
 * with the verdict; opened, the nearest wall in each direction first — the
 * two prices a trader actually watches — then the rest nearest-first, the
 * verdict on the trader's own stop, and what the levels are made from.
 * Memoised: it sits in the chart panel, whose parent renders on every tick.
 */
export const SrLevelsLegend: React.FC<SrLevelsLegendProps> = React.memo(
    function SrLevelsLegend({
        coin,
        levels,
        spot,
        barCount,
        updatedAt,
        isLoading,
        isError,
        trade,
        className,
    }: SrLevelsLegendProps) {
        const [open, setOpen] = useState(false);
        const check = trade ? checkStop(trade, levels, spot) : null;
        const verdict = check && trade ? explainStop(check, trade.type) : null;
        const hasLevels = levels.length > 0;
        const nearest = nearestBySide(levels);
        const others = levels
            .filter((l) => l !== nearest.resistance && l !== nearest.support)
            .sort((a, b) => Math.abs(a.distancePct) - Math.abs(b.distancePct));

        return (
            <div
                className={cn(
                    'pointer-events-auto flex max-w-[min(360px,calc(100%-16px))] flex-col rounded-lg border border-border/70 bg-card/90 text-[11px] shadow-lg backdrop-blur-sm',
                    className,
                )}
                data-slot="sr-levels-legend"
            >
                <div className="flex items-center gap-1.5 px-2 py-1.5">
                    <span className="flex items-center gap-1 px-1.5 py-0.5 font-semibold uppercase tracking-[0.08em] text-foreground">
                        <Ruler size={11} />
                        S/R levels
                    </span>
                    {verdict && hasLevels && (
                        <span
                            className={cn(
                                'flex items-center gap-1 font-semibold',
                                verdict.tone === 'danger' && 'text-destructive',
                                verdict.tone === 'warn' && 'text-amber-300',
                                verdict.tone === 'ok' && 'text-primary',
                            )}
                            title={verdict.text}
                        >
                            {verdict.tone === 'ok' ? (
                                <CheckCircle2 size={11} />
                            ) : (
                                <AlertTriangle size={11} />
                            )}
                            {CHIP_LABEL[verdict.tone]}
                        </span>
                    )}
                    <button
                        type="button"
                        onClick={() => setOpen((o) => !o)}
                        aria-expanded={open}
                        aria-label={
                            open ? 'Hide explanation' : 'What do the levels mean?'
                        }
                        className="ml-auto flex cursor-pointer items-center rounded-md p-1 text-muted-foreground transition-colors hover:text-foreground"
                    >
                        <ChevronDown
                            size={12}
                            className={cn(
                                'transition-transform',
                                open && 'rotate-180',
                            )}
                        />
                    </button>
                </div>

                {open && (
                    <div className="flex flex-col gap-2 border-t border-border/50 px-2.5 py-2 leading-snug">
                        {!hasLevels ? (
                            <p className="text-muted-foreground">
                                {emptyCopy(coin, isLoading, isError)}
                            </p>
                        ) : (
                            <>
                                <p className="text-foreground/90">
                                    Lines mark prices where {coin} has{' '}
                                    <span className="font-semibold">turned before</span>.{' '}
                                    <span className="font-semibold text-destructive">Red</span>{' '}
                                    is resistance — price was pushed back down.{' '}
                                    <span className="font-semibold text-primary">Lime</span>{' '}
                                    is support — price bounced. The more often a level held,
                                    the more it matters.
                                </p>

                                <div className="flex flex-col gap-1">
                                    <NearestRow
                                        title="Next resistance"
                                        coin={coin}
                                        level={nearest.resistance}
                                        icon={<ArrowUp size={11} />}
                                        tone="resistance"
                                    />
                                    <NearestRow
                                        title="Next support"
                                        coin={coin}
                                        level={nearest.support}
                                        icon={<ArrowDown size={11} />}
                                        tone="support"
                                    />
                                </div>

                                {others.length > 0 && (
                                    <div className="flex flex-col gap-0.5">
                                        <div className="text-[9px] font-semibold uppercase tracking-[0.08em] text-muted-foreground">
                                            Further out
                                        </div>
                                        {others.map((l) => (
                                            <LevelRow key={`${l.kind}-${l.price}`} coin={coin} level={l} />
                                        ))}
                                    </div>
                                )}

                                {verdict && (
                                    <p
                                        className={cn(
                                            'rounded-md border px-2 py-1.5 font-medium',
                                            verdict.tone === 'danger' &&
                                                'border-destructive/40 bg-destructive/10 text-destructive',
                                            verdict.tone === 'warn' &&
                                                'border-amber-400/40 bg-amber-400/10 text-amber-200',
                                            verdict.tone === 'ok' &&
                                                'border-primary/30 bg-primary/10 text-primary',
                                        )}
                                        role="status"
                                    >
                                        {verdict.text}
                                    </p>
                                )}
                                {!trade && (
                                    <p className="text-muted-foreground">
                                        Open a position and this card checks your
                                        stop-loss against the levels.
                                    </p>
                                )}

                                <p className="text-[10px] text-muted-foreground">
                                    {explainSource(barCount, updatedAt)}
                                </p>
                            </>
                        )}
                    </div>
                )}
            </div>
        );
    },
);

function emptyCopy(coin: string, isLoading: boolean, isError: boolean): string {
    if (isLoading) return 'Reading the last three days of candles…';
    if (isError) return 'Could not load candles for the levels right now.';
    return `Not enough history on ${coin} yet to find levels — a few hours of trading are needed.`;
}

const toneClass = (kind: Level['kind']) =>
    kind === 'support' ? 'text-primary' : 'text-destructive';

const distance = (l: Level) => `${l.distancePct > 0 ? '+' : ''}${l.distancePct.toFixed(1)}%`;

/** The wall a trader looks at first: big price, distance, how often it held. */
const NearestRow: React.FC<{
    title: string;
    coin: string;
    level: Level | null;
    icon: React.ReactNode;
    tone: Level['kind'];
}> = ({ title, coin, level, icon, tone }) => (
    <div
        className={cn(
            'flex items-center gap-2 rounded-md border px-2 py-1.5',
            tone === 'support'
                ? 'border-primary/25 bg-primary/5'
                : 'border-destructive/25 bg-destructive/5',
        )}
        title={level ? explainLevel(coin, level) : undefined}
    >
        <span className={cn('flex items-center gap-1 text-[9px] font-semibold uppercase tracking-[0.08em]', toneClass(tone))}>
            {icon}
            {title}
        </span>
        {level ? (
            <span className="ml-auto flex items-baseline gap-2 font-mono tabular-nums">
                <span className={cn('text-[12px] font-bold', toneClass(tone))}>
                    ${formatLevelPrice(level.price)}
                </span>
                <span className="text-muted-foreground">{distance(level)}</span>
                <span className={cn('text-[10px]', isStrong(level) ? 'font-bold text-foreground' : 'text-muted-foreground')}>
                    {heldTimes(level.touches)}
                </span>
            </span>
        ) : (
            <span className="ml-auto text-muted-foreground">none in 3 days</span>
        )}
    </div>
);

const LevelRow: React.FC<{ coin: string; level: Level }> = ({ coin, level }) => (
    <div
        className="flex items-baseline gap-2 px-1 font-mono tabular-nums"
        title={explainLevel(coin, level)}
    >
        <span className={cn('h-1.5 w-1.5 shrink-0 self-center rounded-full', level.kind === 'support' ? 'bg-primary' : 'bg-destructive')} />
        <span className={toneClass(level.kind)}>${formatLevelPrice(level.price)}</span>
        <span className="text-muted-foreground">{distance(level)}</span>
        <span className={cn('ml-auto text-[10px]', isStrong(level) ? 'font-bold text-foreground' : 'text-muted-foreground')}>
            {heldTimes(level.touches)}
        </span>
    </div>
);

export default SrLevelsLegend;
